/**
 * AIPersonaSelectionDialog
 *
 * Dialog for selecting AI personas when creating an AI critique session.
 * Feature: 071-ai-critique-sessions
 */

import { useState, useEffect } from 'react';
import { Modal } from '@/components/composed/Modal';
import { Button, Text } from '@/components/atoms';
import { useAvailableAIPersonas, AvailableAIPersona, getProviderDisplayName } from '@/hooks/useAIPersonas';
import { LoadingState, ErrorState } from '@/components/composed';
import logger from '@/utils/logger';

export interface AIPersonaSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedPersonaIds: string[], sessionTitle: string, sessionDescription: string) => void;
  collectionName: string;
  isCreating?: boolean;
}

/**
 * Provider badge colors
 */
function getProviderColor(provider: string): string {
  const colors: Record<string, string> = {
    openai: 'bg-green-100 text-green-800',
    anthropic: 'bg-orange-100 text-orange-800',
    google: 'bg-blue-100 text-blue-800',
  };
  return colors[provider] || 'bg-gray-100 text-gray-800';
}

export function AIPersonaSelectionDialog({
  open,
  onClose,
  onConfirm,
  collectionName,
  isCreating = false,
}: AIPersonaSelectionDialogProps) {
  const { data, refetch, isLoading, isError, error } = useAvailableAIPersonas();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sessionTitle, setSessionTitle] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');

  // Fetch personas when dialog opens
  useEffect(() => {
    if (open) {
      refetch();
      setSessionTitle(`${collectionName} AI Critique`);
      setSessionDescription('');
    }
  }, [open, refetch, collectionName]);

  // Select all personas by default when data loads
  useEffect(() => {
    if (data?.personas) {
      setSelectedIds(new Set(data.personas.map((p) => p.globalId)));
    }
  }, [data?.personas]);

  const handleTogglePersona = (personaId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(personaId)) {
        next.delete(personaId);
      } else {
        next.add(personaId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (data?.personas) {
      setSelectedIds(new Set(data.personas.map((p) => p.globalId)));
    }
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleConfirm = () => {
    const selectedArray = Array.from(selectedIds);
    logger.info('UI', 'AI critique session creation confirmed', {
      personaCount: selectedArray.length,
      title: sessionTitle,
    });
    onConfirm(selectedArray, sessionTitle, sessionDescription);
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose} disabled={isCreating}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleConfirm}
        disabled={selectedIds.size === 0 || !sessionTitle.trim() || isCreating}
      >
        {isCreating ? 'Creating...' : 'Create AI Critique Session'}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create AI Critique Session"
      size="lg"
      footer={footer}
    >
      <div className="space-y-4">
        {/* Session Title */}
        <div>
          <label htmlFor="session-title" className="block text-sm font-medium text-foreground mb-1">
            Session Title
          </label>
          <input
            id="session-title"
            type="text"
            value={sessionTitle}
            onChange={(e) => setSessionTitle(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter session title..."
          />
        </div>

        {/* Session Description (optional - becomes AI context) */}
        <div>
          <label htmlFor="session-description" className="block text-sm font-medium text-foreground mb-1">
            Description <span className="text-muted">(optional - provides context to AI)</span>
          </label>
          <textarea
            id="session-description"
            value={sessionDescription}
            onChange={(e) => setSessionDescription(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            placeholder="E.g., 'Focus on composition and lighting' or 'Looking for feedback on my landscape series'"
            rows={2}
          />
        </div>

        {/* Persona Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-foreground">
              Select AI Critics
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-sm text-primary hover:underline"
              >
                Select All
              </button>
              <span className="text-muted">|</span>
              <button
                type="button"
                onClick={handleDeselectAll}
                className="text-sm text-primary hover:underline"
              >
                Deselect All
              </button>
            </div>
          </div>

          {isLoading && (
            <div className="py-8">
              <LoadingState message="Loading AI critics..." size="sm" />
            </div>
          )}

          {isError && (
            <ErrorState
              title="Failed to load AI critics"
              message={error?.message || 'Could not load available AI personas'}
              onRetry={refetch}
            />
          )}

          {data?.personas && data.personas.length === 0 && (
            <div className="py-8 text-center">
              <Text variant="muted">
                No AI critics available. Please contact an administrator.
              </Text>
            </div>
          )}

          {data?.personas && data.personas.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.personas.map((persona) => (
                <PersonaCheckbox
                  key={persona.globalId}
                  persona={persona}
                  checked={selectedIds.has(persona.globalId)}
                  onChange={() => handleTogglePersona(persona.globalId)}
                />
              ))}
            </div>
          )}
        </div>

        {selectedIds.size === 0 && data?.personas && data.personas.length > 0 && (
          <Text variant="small" className="text-danger">
            Please select at least one AI critic.
          </Text>
        )}
      </div>
    </Modal>
  );
}

/**
 * Individual persona checkbox item
 */
function PersonaCheckbox({
  persona,
  checked,
  onChange,
}: {
  persona: AvailableAIPersona;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
        checked
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-muted hover:bg-muted/10'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{persona.displayName}</span>
          <span className={`px-2 py-0.5 text-xs rounded-full ${getProviderColor(persona.provider)}`}>
            {getProviderDisplayName(persona.provider)}
          </span>
        </div>
        <div className="text-sm text-muted truncate">@{persona.username}</div>
      </div>
    </label>
  );
}
